import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Rating,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  FilterList,
  Download
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getAllEvaluations, getUniqueCourses } from '../services/evaluationService';

function EvaluationRow({ evaluation }) {
  const [open, setOpen] = useState(false);

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'N/A';
      if (timestamp.toDate) {
        return format(timestamp.toDate(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Muito Insatisfeito',
      2: 'Insatisfeito',
      3: 'Regular',
      4: 'Satisfeito',
      5: 'Muito Satisfeito'
    };
    return labels[rating] || 'N/A';
  };

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {formatDate(evaluation.createdAt || evaluation.timestamp)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={evaluation.courseName || 'Sem projeto'}
            size="small"
            color={evaluation.courseName ? 'primary' : 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {evaluation.userId || 'anônimo'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={evaluation.isScorm ? 'Sim' : 'Não'}
            size="small"
            color={evaluation.isScorm ? 'primary' : 'default'}
            variant={evaluation.isScorm ? 'filled' : 'outlined'}
          />
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Rating value={evaluation.rating} readOnly size="small" />
            <Typography variant="caption" color="text.secondary">
              {getRatingLabel(evaluation.rating)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {evaluation.comment || '-'}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom component="div">
                    Detalhes da Avaliação
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Informações Gerais
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" paragraph>
                          <strong>ID:</strong> {evaluation.id}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>Projeto:</strong> {evaluation.courseName || 'Não informado'}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>Usuário:</strong> {evaluation.userId || 'Anônimo'}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>Data:</strong> {formatDate(evaluation.createdAt || evaluation.timestamp)}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>SCORM:</strong> {evaluation.isScorm ? 'Sim' : 'Não'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Nota:</strong> {evaluation.rating}/5 - {getRatingLabel(evaluation.rating)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Opções Selecionadas
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {evaluation.selectedOptions && evaluation.selectedOptions.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {evaluation.selectedOptions.map((option, index) => (
                              <Chip
                                key={index}
                                label={option}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhuma opção selecionada
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Comentário
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {evaluation.comment || 'Nenhum comentário fornecido'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {evaluation.userAgent && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Informações Técnicas
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {evaluation.userAgent}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Função para converter avaliações para CSV
const evaluationsToCSV = (evaluations) => {
  if (!evaluations || evaluations.length === 0) return '';

  // Cabeçalhos
  const headers = [
    'ID',
    'Data',
    'Projeto',
    'Usuário',
    'SCORM',
    'Avaliação',
    'Comentário',
    'Opções Selecionadas'
  ];

  // Converter avaliações para linhas CSV
  const rows = evaluations.map(evaluation => {
    const row = [
      `"${evaluation.id || ''}"`,
      `"${formatDateForCSV(evaluation.createdAt || evaluation.timestamp)}"`,
      `"${evaluation.courseName || ''}"`,
      `"${evaluation.userId || ''}"`,
      `"${evaluation.isScorm ? 'Sim' : 'Não'}"`,
      `"${evaluation.rating || ''}"`,
      `"${(evaluation.comment || '').replace(/"/g, '""')}"`,
      `"${(evaluation.selectedOptions && evaluation.selectedOptions.join('; ') || '').replace(/"/g, '""')}"`
    ];
    return row.join(',');
  });

  // Combinar cabeçalhos e linhas
  return [headers.join(','), ...rows].join('\n');
};

// Função auxiliar para formatar data para CSV
const formatDateForCSV = (timestamp) => {
  try {
    if (!timestamp) return '';
    if (timestamp.toDate) {
      return format(timestamp.toDate(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    }
    if (typeof timestamp === 'string') {
      return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    }
    return '';
  } catch (error) {
    return '';
  }
};

// Função para exportar CSV
const exportToCSV = (data, filename) => {
  const csvContent = evaluationsToCSV(data);
  const blob = new Blob([BOM(csvContent)], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Adiciona BOM para suporte ao Excel
const BOM = (content) => '\uFEFF' + content;

function AllEvaluations() {
  const [evaluations, setEvaluations] = useState([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('todos');
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadEvaluations = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAllEvaluations();
      setEvaluations(data);
      setFilteredEvaluations(data);
    } catch (err) {
      console.error('Erro ao carregar avaliações:', err);
      setError('Erro ao carregar avaliações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const courseList = await getUniqueCourses();
      setCourses(courseList);
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
    }
  };

  const handleExportCSV = () => {
    // Exportar apenas as avaliações filtradas
    exportToCSV(filteredEvaluations, `avaliacoes_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  useEffect(() => {
    loadEvaluations();
    loadCourses();
  }, []);

  useEffect(() => {
    let filtered = evaluations;

    // Apply project filter
    if (selectedCourse !== 'todos') {
      filtered = filtered.filter((evaluation) =>
        evaluation.courseName === selectedCourse
      );
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((evaluation) => {
        return (
          (evaluation.courseName || '').toLowerCase().includes(searchLower) ||
          (evaluation.userId || '').toLowerCase().includes(searchLower) ||
          (evaluation.comment || '').toLowerCase().includes(searchLower) ||
          (evaluation.id || '').toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredEvaluations(filtered);
    setPage(0);
  }, [searchTerm, selectedCourse, evaluations]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadEvaluations} sx={{ ml: 2 }}>
          Tentar Novamente
        </Button>
      </Alert>
    );
  }

  const paginatedEvaluations = filteredEvaluations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Todas as Avaliações
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportCSV}
            disabled={filteredEvaluations.length === 0}
          >
            Exportar CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadEvaluations}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Buscar por projeto, usuário, comentário ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrar por projeto</InputLabel>
            <Select
              value={selectedCourse}
              label="Filtrar por projeto"
              onChange={(e) => setSelectedCourse(e.target.value)}
            >
              <MenuItem value="todos">Todos os projetos</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course} value={course}>
                  {course}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FilterList color="action" />
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredEvaluations.length} de {evaluations.length} avaliações
          </Typography>
        </Box>

        {filteredEvaluations.length === 0 ? (
          <Alert severity="info">
            {searchTerm ? 'Nenhuma avaliação encontrada com os critérios de busca.' : 'Nenhuma avaliação cadastrada ainda.'}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell>Data</TableCell>
                    <TableCell>Projeto</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell align="center">SCORM</TableCell>
                    <TableCell align="center">Avaliação</TableCell>
                    <TableCell>Comentário</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEvaluations.map((evaluation) => (
                    <EvaluationRow key={evaluation.id} evaluation={evaluation} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredEvaluations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default AllEvaluations;
